import { runAggregationOnce } from '@/utils/aggregation';
import {
    buildAggregations,
    parseAggregationResults,
    createSignals,
    buildAppDataFromContext,
    getSubscriptionTimeZone,
} from './signals.utils.js';
import { SIGNAL_TYPES } from './signals.constants.js';
import { getAppId } from '@/vuex/modules/trends/trends-settings.module.js';

const DEFAULT_SIGNAL_TYPES = [
    SIGNAL_TYPES.VISITOR_ADOPTION,
    SIGNAL_TYPES.VISITOR_ENGAGEMENT,
    SIGNAL_TYPES.VISITOR_FRUSTRATION,
    SIGNAL_TYPES.VISITOR_RETENTION,
    SIGNAL_TYPES.ACCOUNT_RETENTION,
    SIGNAL_TYPES.FEATURE_PAGE_ADOPTION,
];

const COUNTABLE_SIGNAL_TYPES = new Set([SIGNAL_TYPES.FEATURE_PAGE_ADOPTION]);

const signals = {
    namespaced: true,
    state: {
        signals: null,
        isLoading: null,
        error: null,
    },
    mutations: {
        setSignals (state, { signals }) {
            state.signals = signals;
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchSignals ({ commit, rootGetters, dispatch }, { types } = {}) {
            commit('setLoading', { isLoading: true });
            commit('setError', { error: null });

            try {
                const signalsToFetch = types?.length ? types : DEFAULT_SIGNAL_TYPES;

                const appId = await getAppId(rootGetters, dispatch);
                const hasShowAnonymousFlag = !!rootGetters['subscription/getFeatureFlag']('showAnonymous');

                const needsCountables = signalsToFetch.some((type) => COUNTABLE_SIGNAL_TYPES.has(type));
                if (needsCountables) {
                    await Promise.all([
                        dispatch('pages/loadAll', {}, { root: true }),
                        dispatch('features/loadAll', {}, { root: true }),
                    ]);
                }

                const countableIds = needsCountables
                    ? (rootGetters['trendsSettings/getEvents']?.map((event) => ({ id: event.id, kind: event.kind })) ?? [])
                    : [];

                const context = {
                    appId,
                    hasShowAnonymousFlag,
                    countableIds,
                    appData: buildAppDataFromContext(rootGetters, appId),
                    timeZone: getSubscriptionTimeZone(rootGetters),
                };

                const [aggregations, breakpoints] = buildAggregations(signalsToFetch, context);

                const rawData = await Promise.all(
                    aggregations.map((agg) => runAggregationOnce(agg)),
                );

                const extractedData = parseAggregationResults(signalsToFetch, breakpoints, rawData);
                const signals = createSignals(signalsToFetch, extractedData, context);

                commit('setSignals', { signals });
                commit('setLoading', { isLoading: false });

                return signals;
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch signals' });
                commit('setLoading', { isLoading: false });
                throw error;
            }
        },
    },
    getters: {
        signals: (state) => state.signals || [],
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
    },
};

export default signals;
